import { useEffect, useRef, useState } from "react";
import { kioskPayStatus, kioskPayRetry, kioskPayCancel } from "@/lib/api";
import { formatMoney } from "@/lib/orderUtils";

// Full-screen "pay on the reader" state. Calls onPaid(orderId) once Stripe confirms.
export const TerminalPayOverlay = ({ payment, readerLabel, onPaid, onClose }) => {
    const [state, setState] = useState({ status: "in_progress", message: "" });
    const done = useRef(false);

    useEffect(() => {
        const t = setInterval(async () => {
            if (done.current) return;
            try {
                const s = await kioskPayStatus(payment.payment_id);
                if (s.status === "succeeded") { done.current = true; clearInterval(t); onPaid(s.order_id); return; }
                setState({ status: s.status, message: s.message || "" });
            } catch { /* keep polling */ }
        }, 1500);
        return () => clearInterval(t);
    }, [payment.payment_id, onPaid]);

    const retry = async () => { setState({ status: "in_progress", message: "" }); try { await kioskPayRetry(payment.payment_id); } catch (e) { setState({ status: "failed", message: e.response?.data?.detail || "Couldn't retry" }); } };
    const cancel = async () => {
        const r = await kioskPayCancel(payment.payment_id).catch(() => ({}));
        if (r.status === "succeeded") { done.current = true; onPaid(r.order_id); } else onClose();
    };
    const failed = state.status === "failed";

    return (
        <div className="fixed inset-0 z-[70] bg-[var(--bg)]/95 backdrop-blur grid place-items-center p-8 text-center" data-testid="terminal-pay-overlay">
            <div className="max-w-xl">
                <p className="eyebrow">// {readerLabel || "Card reader"}</p>
                <div className="font-display text-[84px] leading-none text-gold my-6" data-testid="terminal-pay-amount">{formatMoney(payment.amount)}</div>
                {failed ? (
                    <>
                        <p className="font-display text-[34px] text-[#ff5246] mb-2" data-testid="terminal-pay-failed">Payment didn't go through</p>
                        <p className="text-muted mb-8">{state.message}</p>
                        <div className="flex gap-3 justify-center">
                            <button className="btn-primary text-base px-8 py-4" onClick={retry} data-testid="terminal-pay-retry">Try again</button>
                            <button className="btn-secondary text-base px-8 py-4" onClick={cancel} data-testid="terminal-pay-cancel">Cancel</button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="font-display text-[38px] leading-tight mb-3" data-testid="terminal-pay-prompt">Tap, insert or use your phone<br />on the card reader →</p>
                        <p className="font-mono text-[12px] uppercase tracking-[0.2em] text-muted mb-10">{state.message || "Waiting for card…"}</p>
                        <button className="btn-ghost" onClick={cancel} data-testid="terminal-pay-cancel">Cancel payment</button>
                    </>
                )}
            </div>
        </div>
    );
};
